import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    IconButton,
    Select,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import { Link } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ReplayIcon from "@mui/icons-material/Replay";
import axios from "axios";
import * as XLSX from "xlsx";
import RichTextEditor from "../Utiles/RichTextEditor.jsx";

// ---- normalizadores ----
const normSubvencion = (s) => ({
    id: s.id,
    nombre: s.nombre,
    descripcion: s.descripcion,
    numArchivos: Number(s.numArchivos ?? s.num_archivos ?? 0),
    estadoConvocatoria: s.estadoConvocatoria ?? s.estado_convocatoria ?? "ACTIVA",
});

const normSolicitante = (r) => ({
    colegiadoCodigo: r.colegiadoCodigo ?? r.colegiado_codigo ?? null,
    nombreCompleto: r.nombreCompleto ?? r.nombre_completo ?? "",
    subvencionId: r.subvencionId ?? r.subvencion_id ?? null,
    subvencionNombre: r.subvencionNombre ?? r.subvencion_nombre ?? "",
    estado: r.estado ?? null,
});

// NUEVO: normalizador de filas de la tabla subvencion_solicitudes
const normSolicitudRegistro = (s) => ({
    id: s.id,
    colegiadoId: s.colegiadoId ?? s.colegiado_id ?? null, // código colegiado
    // campos que intentaremos completar si no vienen del backend
    nombreCompleto: s.nombreCompleto ?? s.nombre_completo ?? "",
    localidad: s.localidad ?? "",
    provincia: s.provincia ?? "",
    // existentes
    subvencionId: s.subvencionId ?? s.subvencion_id ?? null,
    nombreCongreso: s.nombreCongreso ?? s.nombre_congreso ?? "",
    tituloPonencia: s.tituloPonencia ?? s.titulo_ponencia ?? "",
    autores: s.autores ?? "",
    importe: Number(s.importe ?? 0),
    creadoEl: s.creadoEl ?? s.creado_el ?? null,
    iban: s.iban ?? "",
    total: Number(s.total ?? s.total_importe ?? 0),
});

const normPage = (p) => ({
    content: Array.isArray(p?.content) ? p.content : Array.isArray(p) ? p : [],
    number: p?.number ?? p?.page ?? 0,
    size: p?.size ?? p?.page_size ?? 10,
    totalPages: p?.totalPages ?? p?.total_pages ?? (Array.isArray(p) ? 1 : 0),
    totalElements: p?.totalElements ?? p?.total_elements ?? (Array.isArray(p) ? p.length : 0),
});

const SubvencionesAdmin = () => {
    const API = process.env.REACT_APP_API_URL || "http://localhost:8080";
    const PUBLIC_BASE = API.replace(/\/api\/?$/, "");
    const token = localStorage.getItem("token");
    const headersAuth = () => ({ Authorization: `Bearer ${token}` });

    // Caché de detalles de colegiado por código (en memoria)
    const colegiadoCache = useRef(new Map());

    const toPublicUrl = (serverUrl) =>
        serverUrl?.startsWith("http") ? serverUrl : `${PUBLIC_BASE}${serverUrl}`;

    // ---- catálogo subvenciones ----
    const [subvenciones, setSubvenciones] = useState([]);

    // ---- modal crear/editar ----
    const [openDialog, setOpenDialog] = useState(false);
    const [editando, setEditando] = useState(null);
    const [plantillas, setPlantillas] = useState([]);
    const [formData, setFormData] = useState({
        nombre: "",
        descripcion: "",
        numArchivos: 1,
        estadoConvocatoria: "ACTIVA",
    });

    // ---- ver descripción ----
    const [modalAbierto, setModalAbierto] = useState(false);
    const [subvencionSeleccionada, setSubvencionSeleccionada] = useState(null);

    // ---- solicitantes (colegiado_subvenciones) ----
    const [mostrarColegios, setMostrarColegios] = useState(false);
    const [rowsPage, setRowsPage] = useState({
        content: [],
        number: 0,
        size: 10,
        totalPages: 0,
        totalElements: 0,
    });
    const [loadingSolicitantes, setLoadingSolicitantes] = useState(false);
    const [estadosActuales, setEstadosActuales] = useState({});
    const [filtroSubvId, setFiltroSubvId] = useState("");
    const [filtroEstado, setFiltroEstado] = useState(""); // "", PENDIENTE, CONCEDIDA, DENEGADA

    // ---- NUEVO: solicitudes (subvencion_solicitudes) ----
    const [mostrarSolicitudes, setMostrarSolicitudes] = useState(false);
    const [solicitudesPage, setSolicitudesPage] = useState({
        content: [],
        number: 0,
        size: 10,
        totalPages: 0,
        totalElements: 0,
    });
    const [loadingSolicitudes, setLoadingSolicitudes] = useState(false);
    const [filtroSubvIdSol, setFiltroSubvIdSol] = useState("");
    const [filtroColegiadoIdSol, setFiltroColegiadoIdSol] = useState("");
    const [filtroTextoSol, setFiltroTextoSol] = useState(""); // busca en congreso/ponencia/autores

    // helper para mostrar nombre de subvención por id
    const subvencionNombrePorId = (id) =>
        subvenciones.find((s) => String(s.id) === String(id))?.nombre || `ID ${id}`;

    // =================== fetch catálogo ===================
    const fetchSubvenciones = async () => {
        try {
            const res = await axios.get(`${API}/api/subvenciones`, {
                headers: headersAuth(),
            });
            const list = Array.isArray(res.data) ? res.data.map(normSubvencion) : [];
            setSubvenciones(list);
        } catch (e) {
            console.error(e);
            setSubvenciones([]);
        }
    };

    useEffect(() => {
        fetchSubvenciones();
    }, []);

    // ========================= Obtener datos de colegiado (con caché) =========================
    const fetchColegiadoDetalles = async (codigoColegiado) => {
        if (!codigoColegiado) return null;
        if (colegiadoCache.current.has(codigoColegiado))
            return colegiadoCache.current.get(codigoColegiado);

        try {
            const { data } = await axios.get(`${API}/api/admin/colegiados/${codigoColegiado}`, {
                headers: headersAuth(),
            });
            const c = {
                nombre: data?.nombre ?? "",
                apellido_1: data?.apellido_1 ?? "",
                apellido_2: data?.apellido_2 ?? "",
                localidad: data?.localidad ?? "",
                provincia: data?.provincia ?? "",
                email: data?.email ?? "",
            };
            colegiadoCache.current.set(codigoColegiado, c);
            return c;
        } catch {
            colegiadoCache.current.set(codigoColegiado, null);
            return null;
        }
    };

    // =================== CRUD subvenciones ===================

    const handleOpen = (subv = null) => {
        if (subv) {
            const n = normSubvencion(subv);
            setEditando(n.id);
            setFormData({
                nombre: n.nombre,
                descripcion: n.descripcion,
                numArchivos: n.numArchivos,
                estadoConvocatoria: n.estadoConvocatoria,
            });
        } else {
            setEditando(null);
            setFormData({
                nombre: "",
                descripcion: "",
                numArchivos: 1,
                estadoConvocatoria: "ACTIVA",
            });
            setPlantillas([]);
        }
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setPlantillas([]);
    };

    const absolutizePublicLinks = (html) =>
        (html || "").replace(/href="\/public\//g, `href="${PUBLIC_BASE}/public/`);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((p) => ({ ...p, [name]: value }));
    };

    const handleSubmit = async () => {
        const payload = {
            nombre: formData.nombre,
            descripcion: absolutizePublicLinks(formData.descripcion),
            // importante: SNAKE_CASE + forzar número
            num_archivos: Number(formData.numArchivos),
            estado_convocatoria: formData.estadoConvocatoria,
        };

        try {
            if (editando) {
                await axios.put(`${API}/api/subvenciones/${editando}`, payload, {
                    headers: headersAuth(),
                });
            } else {
                await axios.post(`${API}/api/subvenciones`, payload, {
                    headers: headersAuth(),
                });
            }
            await fetchSubvenciones();
            handleClose();
        } catch (e) {
            console.error(e);
            alert("Error guardando la subvención");
        }
    };

    const handleEliminar = async (id) => {
        if (!window.confirm("¿Eliminar esta subvención?")) return;
        try {
            await axios.delete(`${API}/api/subvenciones/${id}`, {
                headers: headersAuth(),
            });
            await fetchSubvenciones();
        } catch (e) {
            console.error(e);
            alert("No se pudo eliminar.");
        }
    };

    // =================== FINALIZAR / REABRIR ===================

    const finalizarConvocatoria = async (id) => {
        if (!window.confirm("¿Marcar esta convocatoria como FINALIZADA?")) return;
        try {
            await axios.put(`${API}/api/subvenciones/${id}/finalizar`, null, {
                headers: headersAuth(),
            });
            await fetchSubvenciones();
        } catch (e) {
            console.error(e);
            alert("No se pudo finalizar la convocatoria.");
        }
    };

    const reabrirConvocatoria = async (id) => {
        try {
            await axios.put(`${API}/api/subvenciones/${id}/reabrir`, null, {
                headers: headersAuth(),
            });
            await fetchSubvenciones();
        } catch (e) {
            console.error(e);
            alert("No se pudo reabrir la convocatoria.");
        }
    };

    // =================== Ver descripción ===================

    const handleVerDescripcion = (subv) => {
        setSubvencionSeleccionada(normSubvencion(subv));
        setModalAbierto(true);
    };

    // =================== Plantillas públicas (PDF) ===================

    const handlePlantillasChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const uploadAxios = axios.create();
        try {
            const nombreSubv = (formData.nombre ?? "Subvencion").trim();
            const promesas = files.map(async (f) => {
                if (f.type !== "application/pdf") {
                    throw new Error(`El archivo ${f.name} no es un PDF`);
                }
                const fd = new FormData();
                fd.append("file", f, f.name);
                fd.append("nombreSubvencion", nombreSubv);

                const res = await uploadAxios.post(
                    `${API}/api/documentos/admin/plantillas`,
                    fd,
                    {
                        headers: headersAuth(),
                        transformRequest: (d) => d,
                    }
                );
                return res.data; // { url, filename }
            });

            const nuevas = await Promise.all(promesas);
            setPlantillas((prev) => [...prev, ...nuevas]);
        } catch (err) {
            console.error(err);
            alert(err.message || "Error subiendo plantillas");
        } finally {
            e.target.value = null;
        }
    };

    // =================== Solicitantes (paginado + filtro + export) ===================

    const cargarColegiadosConSubvenciones = async (page = 0, size = rowsPage.size) => {
        setLoadingSolicitantes(true);
        try {
            const res = await axios.get(
                `${API}/api/subvenciones/colegiados/subvenciones`,
                {
                    params: {
                        page,
                        size,
                        subvencionId: filtroSubvId || undefined,
                        estado: filtroEstado || undefined, // PENDIENTENTE | CONCEDIDA | DENEGADA
                    },
                    headers: headersAuth(),
                }
            );

            const p = normPage(res.data);
            const contentNorm = (p.content || []).map(normSolicitante);
            setRowsPage({ ...p, content: contentNorm });
            setMostrarColegios(true);
        } catch (e) {
            console.error(e);
            setRowsPage(
                normPage({
                    content: [],
                    number: 0,
                    size,
                    totalPages: 0,
                    totalElements: 0,
                })
            );
        } finally {
            setLoadingSolicitantes(false);
        }
    };

    const handleChangeEstadoLocal = (codigo, subvId, nuevoEstado) => {
        setEstadosActuales((prev) => ({
            ...prev,
            [`${codigo}_${subvId}`]: nuevoEstado,
        }));
    };

    const actualizarEstado = async (codigo, subvId) => {
        const estadoSeleccionado = estadosActuales[`${codigo}_${subvId}`] ?? "PENDIENTE";
        try {
            await axios.put(
                `${API}/api/subvenciones/estado`,
                null,
                {
                    params: {
                        colegiadoCodigo: codigo,
                        subvencionId: subvId,
                        estado: estadoSeleccionado,
                    },
                    headers: headersAuth(),
                }
            );
            alert("Estado actualizado");
        } catch (err) {
            console.error(err);
            alert("Error al actualizar estado");
        }
    };

    // Exportar todas las filas (por filtro) — COLEGIADO_SUBVENCIONES
    const exportarExcel = async () => {
        try {
            const size = 1000;
            let page = 0;
            let todos = [];

            for (let i = 0; i < 200; i++) {
                const res = await axios.get(
                    `${API}/api/subvenciones/colegiados/subvenciones`,
                    {
                        params: {
                            page,
                            size,
                            subvencionId: filtroSubvId || undefined,
                            estado: filtroEstado || undefined,
                        },
                        headers: headersAuth(),
                    }
                );
                const p = normPage(res.data);
                const contentNorm = (p.content || []).map(normSolicitante);
                todos = todos.concat(contentNorm);
                if (page + 1 >= p.totalPages) break;
                page++;
            }

            const rows = todos.map((r) => ({
                Código: r.colegiadoCodigo ?? "",
                Nombre: r.nombreCompleto ?? "",
                Subvención: r.subvencionNombre ?? "",
                Estado: r.estado ?? "",
            }));

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(rows);
            XLSX.utils.book_append_sheet(wb, ws, "Solicitantes");
            XLSX.writeFile(
                wb,
                `solicitantes_${filtroSubvId || "todas"}_${filtroEstado || "todos"}.xlsx`
            );
        } catch (e) {
            console.error(e);
            alert("No se pudo exportar a Excel.");
        }
    };

    // =================== NUEVO: Solicitudes (subvencion_solicitudes) ===================

    const cargarSolicitudes = async (page = 0, size = solicitudesPage.size) => {
        setLoadingSolicitudes(true);
        try {
            const res = await axios.get(`${API}/api/subvenciones/solicitudes`, {
                params: {
                    page,
                    size,
                    subvencionId: filtroSubvIdSol || undefined,
                    colegiadoId: filtroColegiadoIdSol || undefined,
                    q: filtroTextoSol || undefined, // busca en congreso/ponencia/autores (si lo implementas en backend)
                },
                headers: headersAuth(),
            });

            const p = normPage(res.data);
            const contentNorm = (p.content || []).map(normSolicitudRegistro);

            // Enriquecer con datos de colegiado (nombre completo, localidad, provincia)
            const codes = [...new Set(contentNorm.map((s) => s.colegiadoId).filter(Boolean))];

            // Traemos detalles de los que no estén cacheados aún
            const fetchMissing = codes
                .filter((c) => !colegiadoCache.current.has(c))
                .map((c) => fetchColegiadoDetalles(c));
            await Promise.all(fetchMissing);

            // Rellenar los campos en cada fila con lo que hay (o se acaba de traer) en caché
            for (const s of contentNorm) {
                if (!s.colegiadoId) continue;
                const c = colegiadoCache.current.get(s.colegiadoId);
                if (c) {
                    const nombreCompleto = `${c.nombre ?? ""} ${c.apellido_1 ?? ""} ${c.apellido_2 ?? ""}`.trim();
                    s.nombreCompleto = s.nombreCompleto || nombreCompleto;
                    s.localidad = s.localidad || c.localidad || "";
                    s.provincia = s.provincia || c.provincia || "";
                }
            }

            setSolicitudesPage({ ...p, content: contentNorm });
            setMostrarSolicitudes(true);
        } catch (e) {
            console.error(e);
            setSolicitudesPage(
                normPage({
                    content: [],
                    number: 0,
                    size,
                    totalPages: 0,
                    totalElements: 0,
                })
            );
        } finally {
            setLoadingSolicitudes(false);
        }
    };

    // Exportar solicitudes a Excel (incluye nombre/localidad/provincia con caché)
    const exportarSolicitudesExcel = async () => {
        try {
            const size = 1000;
            let page = 0;
            let todos = [];

            for (let i = 0; i < 200; i++) {
                const res = await axios.get(`${API}/api/subvenciones/solicitudes`, {
                    params: {
                        page,
                        size,
                        subvencionId: filtroSubvIdSol || undefined,
                        colegiadoId: filtroColegiadoIdSol || undefined,
                        q: filtroTextoSol || undefined,
                    },
                    headers: headersAuth(),
                });
                const p = normPage(res.data);
                const contentNorm = (p.content || []).map(normSolicitudRegistro);
                todos = todos.concat(contentNorm);
                if (page + 1 >= p.totalPages) break;
                page++;
            }

            // Asegurar que el caché tenga los colegiados necesarios
            const codes = [...new Set(todos.map((s) => s.colegiadoId).filter(Boolean))];
            const missing = codes.filter((c) => !colegiadoCache.current.has(c));
            if (missing.length) {
                await Promise.all(missing.map((c) => fetchColegiadoDetalles(c)));
            }

            const rows = todos.map((s) => {
                const c = s.colegiadoId ? colegiadoCache.current.get(s.colegiadoId) : null;
                const nombreCompleto =
                    s.nombreCompleto ||
                    (c ? `${c.nombre ?? ""} ${c.apellido_1 ?? ""} ${c.apellido_2 ?? ""}`.trim() : "");
                const localidad = s.localidad || c?.localidad || "";
                const provincia = s.provincia || c?.provincia || "";

                return {
                    Fecha: s.creadoEl ?? "",
                    "Código colegiado": s.colegiadoId ?? "",
                    "Nombre completo": nombreCompleto,
                    Localidad: localidad,
                    Provincia: provincia,
                    IBAN: s.iban ?? "",
                    "ID Subvención": s.subvencionId ?? "",
                    "Nombre Subvención": subvencionNombrePorId(s.subvencionId),
                    "Nombre congreso": s.nombreCongreso,
                    "Título ponencia": s.tituloPonencia,
                    Autores: s.autores,
                    Importe: s.importe,
                    "Total por colegiado/subvención": s.total,
                };
            });

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(rows);
            XLSX.utils.book_append_sheet(wb, ws, "Solicitudes");
            XLSX.writeFile(
                wb,
                `solicitudes_${filtroSubvIdSol || "todas"}_${filtroColegiadoIdSol || "todos"}.xlsx`
            );
        } catch (e) {
            console.error(e);
            alert("No se pudo exportar a Excel.");
        }
    };

    // =================== RENDER ===================

    return (
        <Paper elevation={3} sx={{ padding: 3, margin: 2 }}>
            <Box p={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5">Gestión de Subvenciones</Typography>
                    <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
                        Nueva Subvención
                    </Button>
                </Box>

                {subvenciones.length === 0 ? (
                    <Typography>No hay subvenciones registradas.</Typography>
                ) : (
                    subvenciones.map((subv0) => {
                        const subv = normSubvencion(subv0);
                        const esFinalizada = subv.estadoConvocatoria === "FINALIZADA";
                        return (
                            <Box key={subv.id} className="border rounded p-3 mb-3">
                                <div className="flex flex-col space-y-1">
                                    <div className="flex gap-2">
                                        <IconButton onClick={() => handleOpen(subv)} size="small" title="Editar">
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton onClick={() => handleEliminar(subv.id)} size="small" title="Eliminar">
                                            <Delete fontSize="small" />
                                        </IconButton>
                                        <IconButton onClick={() => handleVerDescripcion(subv)} size="small" title="Ver descripción">
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>

                                        {esFinalizada ? (
                                            <Button size="small" startIcon={<ReplayIcon />} onClick={() => reabrirConvocatoria(subv.id)}>
                                                Reabrir
                                            </Button>
                                        ) : (
                                            <Button color="warning" size="small" startIcon={<DoneAllIcon />} onClick={() => finalizarConvocatoria(subv.id)}>
                                                Finalizar
                                            </Button>
                                        )}
                                    </div>

                                    <Typography className="font-semibold text-base">
                                        {subv.nombre}
                                        <span className="ml-2 text-sm text-gray-500 font-normal">
                                            Requiere {subv.numArchivos} archivo(s)
                                        </span>
                                        <span
                                            className={`ml-3 text-xs px-2 py-0.5 rounded ${esFinalizada ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"
                                                }`}
                                        >
                                            {esFinalizada ? "FINALIZADA" : "ACTIVA"}
                                        </span>
                                    </Typography>

                                    <div
                                        className="tiptap-rendered max-h-24 overflow-hidden text-sm"
                                        dangerouslySetInnerHTML={{ __html: subv.descripcion }}
                                    />
                                </div>
                            </Box>
                        );
                    })
                )}

                {/* Diálogo crear/editar */}
                <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="md">
                    <DialogTitle>{editando ? "Editar" : "Nueva"} Subvención</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Nombre"
                            name="nombre"
                            value={formData.nombre}
                            onChange={handleChange}
                            fullWidth
                            margin="dense"
                        />

                        <RichTextEditor
                            value={formData.descripcion}
                            onChange={(val) => setFormData((prev) => ({ ...prev, descripcion: val }))}
                        />

                        <TextField
                            label="Nº Archivos Requeridos"
                            name="numArchivos"
                            type="number"
                            value={formData.numArchivos}
                            onChange={handleChange}
                            fullWidth
                            margin="dense"
                            inputProps={{ min: 0 }}
                        />

                        <Select
                            fullWidth
                            size="small"
                            value={formData.estadoConvocatoria}
                            onChange={(e) => setFormData((p) => ({ ...p, estadoConvocatoria: e.target.value }))}
                            sx={{ mt: 1 }}
                        >
                            <MenuItem value="ACTIVA">ACTIVA</MenuItem>
                            <MenuItem value="FINALIZADA">FINALIZADA</MenuItem>
                        </Select>

                        {/* Plantillas públicas */}
                        <Box mt={2}>
                            <Typography variant="subtitle2">Plantillas públicas (PDF)</Typography>
                            <input type="file" accept="application/pdf" multiple onChange={handlePlantillasChange} />

                            <Box mt={1}>
                                {plantillas.map((p, idx) => (
                                    <Box key={idx} display="flex" alignItems="center" gap={1} mb={0.5}>
                                        <Typography variant="body2">{p.filename}</Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                                const enlace = `<a href="${toPublicUrl(p.url)}" target="_blank" rel="noopener noreferrer">${p.filename}</a>`;
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    descripcion: (prev.descripcion || "") + `<p>${enlace}</p>`,
                                                }));
                                            }}
                                        >
                                            Insertar en descripción
                                        </Button>
                                    </Box>
                                ))}
                            </Box>

                            {plantillas.length > 0 && (
                                <Button
                                    size="small"
                                    sx={{ mt: 1 }}
                                    onClick={() => {
                                        const lista = plantillas
                                            .map(
                                                (p) =>
                                                    `<li><a href="${toPublicUrl(p.url)}" target="_blank" rel="noopener noreferrer">${p.filename}</a></li>`
                                            )
                                            .join("");
                                        setFormData((prev) => ({
                                            ...prev,
                                            descripcion: (prev.descripcion || "") + `<ul>${lista}</ul>`,
                                        }));
                                    }}
                                >
                                    Insertar todos en descripción
                                </Button>
                            )}
                        </Box>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={handleClose}>Cancelar</Button>
                        <Button onClick={handleSubmit} variant="contained">
                            Guardar
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Solicitantes (colegiado_subvenciones) */}
                <Box mt={4}>
                    <Box display="flex" gap={1} alignItems="center" mb={1}>
                        <Button variant="outlined" onClick={() => cargarColegiadosConSubvenciones()}>
                            Ver colegiados con subvenciones
                        </Button>

                        {/* Filtro por subvención */}
                        <Select
                            size="small"
                            value={filtroSubvId}
                            onChange={(e) => {
                                setFiltroSubvId(e.target.value);
                                setRowsPage((p) => ({ ...p, number: 0 }));
                            }}
                            displayEmpty
                            sx={{ minWidth: 260 }}
                        >
                            <MenuItem value="">
                                <em>Todos</em>
                            </MenuItem>
                            {subvenciones.map((s) => (
                                <MenuItem key={s.id} value={s.id}>
                                    {s.nombre}
                                </MenuItem>
                            ))}
                        </Select>

                        {/* Filtro por estado */}
                        <Select
                            size="small"
                            value={filtroEstado}
                            onChange={(e) => {
                                setFiltroEstado(e.target.value);
                                setRowsPage((p) => ({ ...p, number: 0 }));
                            }}
                            sx={{ minWidth: 150 }}
                            displayEmpty
                        >
                            <MenuItem value="">
                                <em>Todos los estados</em>
                            </MenuItem>
                            <MenuItem value="PENDIENTE">PENDIENTE</MenuItem>
                            <MenuItem value="CONCEDIDA">CONCEDIDA</MenuItem>
                            <MenuItem value="DENEGADA">DENEGADA</MenuItem>
                        </Select>

                        <Button variant="contained" onClick={() => cargarColegiadosConSubvenciones(0)}>
                            Aplicar filtro
                        </Button>

                        <Button onClick={exportarExcel}>Exportar Excel</Button>
                    </Box>

                    {mostrarColegios && (
                        <>
                            {loadingSolicitantes ? (
                                <Typography>Cargando…</Typography>
                            ) : (
                                <>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={{ width: 110 }}>Código</TableCell>
                                                    <TableCell>Nombre</TableCell>
                                                    <TableCell>Subvención</TableCell>
                                                    <TableCell style={{ width: 180 }}>Estado</TableCell>
                                                    <TableCell align="right" style={{ width: 120 }}>
                                                        Acciones
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {rowsPage.content?.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={5}>
                                                            <Typography>No hay solicitudes.</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    rowsPage.content?.map((r) => (
                                                        <TableRow key={`${r.colegiadoCodigo}_${r.subvencionId}`}>
                                                            <TableCell>{r.colegiadoCodigo ?? ""}</TableCell>
                                                            <TableCell>{r.nombreCompleto?.trim() || ""}</TableCell>

                                                            {/* Enlace a documentos del colegiado */}
                                                            <TableCell>
                                                                <Link
                                                                    to={`/admin/documentos?colegiado=${encodeURIComponent(r.colegiadoCodigo ?? "")}`}
                                                                    title="Ver documentos del colegiado"
                                                                    style={{ textDecoration: "underline" }}
                                                                >
                                                                    {r.subvencionNombre ?? ""}
                                                                </Link>
                                                            </TableCell>

                                                            <TableCell>
                                                                <Select
                                                                    size="small"
                                                                    value={
                                                                        estadosActuales[`${r.colegiadoCodigo}_${r.subvencionId}`] ??
                                                                        r.estado ??
                                                                        "PENDIENTE"
                                                                    }
                                                                    onChange={(e) =>
                                                                        handleChangeEstadoLocal(
                                                                            r.colegiadoCodigo,
                                                                            r.subvencionId,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    sx={{ minWidth: 150 }}
                                                                >
                                                                    <MenuItem value="PENDIENTE">PENDIENTE</MenuItem>
                                                                    <MenuItem value="CONCEDIDA">CONCEDIDA</MenuItem>
                                                                    <MenuItem value="DENEGADA">DENEGADA</MenuItem>
                                                                </Select>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onClick={() =>
                                                                        actualizarEstado(r.colegiadoCodigo, r.subvencionId)
                                                                    }
                                                                >
                                                                    Guardar
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <TablePagination
                                        component="div"
                                        count={rowsPage.totalElements}
                                        page={rowsPage.number}
                                        onPageChange={(_e, newPage) =>
                                            cargarColegiadosConSubvenciones(newPage, rowsPage.size)
                                        }
                                        rowsPerPage={rowsPage.size}
                                        onRowsPerPageChange={(e) =>
                                            cargarColegiadosConSubvenciones(0, parseInt(e.target.value, 10))
                                        }
                                        rowsPerPageOptions={[5, 10, 20, 50, 100]}
                                        labelRowsPerPage="Filas por página"
                                        labelDisplayedRows={({ from, to, count }) =>
                                            `${Number.isNaN(from) ? 0 : from}–${Number.isNaN(to) ? 0 : to} de ${count}`
                                        }
                                    />
                                </>
                            )}
                        </>
                    )}
                </Box>

                {/* =================== NUEVA SECCIÓN: Solicitudes registradas (subvencion_solicitudes) =================== */}
                <Box mt={6}>
                    <Typography variant="h6" gutterBottom>Solicitudes registradas</Typography>

                    <Box display="flex" gap={1} alignItems="center" mb={1} flexWrap="wrap">
                        <Button variant="outlined" onClick={() => cargarSolicitudes()}>
                            Ver solicitudes
                        </Button>

                        {/* Filtro por subvención */}
                        <Select
                            size="small"
                            value={filtroSubvIdSol}
                            onChange={(e) => {
                                setFiltroSubvIdSol(e.target.value);
                                setSolicitudesPage((p) => ({ ...p, number: 0 }));
                            }}
                            displayEmpty
                            sx={{ minWidth: 260 }}
                        >
                            <MenuItem value="">
                                <em>Todas las subvenciones</em>
                            </MenuItem>
                            {subvenciones.map((s) => (
                                <MenuItem key={s.id} value={s.id}>
                                    {s.nombre}
                                </MenuItem>
                            ))}
                        </Select>

                        {/* Filtro por colegiado */}
                        <TextField
                            size="small"
                            label="Código colegiado"
                            value={filtroColegiadoIdSol}
                            onChange={(e) => setFiltroColegiadoIdSol(e.target.value)}
                            sx={{ width: 180 }}
                        />

                        {/* Búsqueda texto libre */}
                        <TextField
                            size="small"
                            label="Buscar (congreso/ponencia/autores)"
                            value={filtroTextoSol}
                            onChange={(e) => setFiltroTextoSol(e.target.value)}
                            sx={{ minWidth: 320 }}
                        />

                        <Button variant="contained" onClick={() => cargarSolicitudes(0)}>
                            Aplicar filtro
                        </Button>

                        <Button onClick={exportarSolicitudesExcel}>Exportar Excel</Button>
                    </Box>

                    {mostrarSolicitudes && (
                        <>
                            {loadingSolicitudes ? (
                                <Typography>Cargando…</Typography>
                            ) : (
                                <>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={{ width: 165 }}>Fecha</TableCell>
                                                    <TableCell style={{ width: 120 }}>Cód. colegiado</TableCell>
                                                    {/* nuevas columnas */}
                                                    <TableCell>Nombre completo</TableCell>
                                                    <TableCell>Localidad</TableCell>
                                                    <TableCell>Provincia</TableCell>
                                                    {/* existentes */}
                                                    <TableCell>IBAN</TableCell>
                                                    <TableCell>Subvención</TableCell>
                                                    <TableCell>Congreso</TableCell>
                                                    <TableCell>Ponencia</TableCell>
                                                    <TableCell>Autores</TableCell>
                                                    <TableCell align="right" style={{ width: 110 }}>Importe (€)</TableCell>
                                                    <TableCell align="right" style={{ width: 130 }}>Total (€)</TableCell>
                                                    <TableCell align="right" style={{ width: 120 }}>Acciones</TableCell>
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {solicitudesPage.content?.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={13}>
                                                            <Typography>No hay registros.</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    solicitudesPage.content?.map((s) => (
                                                        <TableRow key={s.id}>
                                                            <TableCell>{s.creadoEl ? String(s.creadoEl).replace("T", " ").slice(0, 19) : ""}</TableCell>
                                                            <TableCell>{s.colegiadoId ?? ""}</TableCell>
                                                            {/* celdas nuevas */}
                                                            <TableCell title={s.nombreCompleto}>{s.nombreCompleto || ""}</TableCell>
                                                            <TableCell title={s.localidad}>{s.localidad || ""}</TableCell>
                                                            <TableCell title={s.provincia}>{s.provincia || ""}</TableCell>
                                                            {/* existentes */}
                                                            <TableCell>{s.iban || ""}</TableCell>
                                                            <TableCell>{subvencionNombrePorId(s.subvencionId)}</TableCell>
                                                            <TableCell title={s.nombreCongreso}>{s.nombreCongreso}</TableCell>
                                                            <TableCell title={s.tituloPonencia}>{s.tituloPonencia}</TableCell>
                                                            <TableCell title={s.autores}>{s.autores}</TableCell>
                                                            <TableCell align="right">
                                                                {Number.isFinite(s.importe) ? s.importe.toFixed(2) : ""}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {Number.isFinite(s.total) ? s.total.toFixed(2) : ""}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Link
                                                                    to={`/admin/documentos?colegiado=${encodeURIComponent(s.colegiadoId ?? "")}`}
                                                                    title="Ver documentos del colegiado"
                                                                    style={{ textDecoration: "underline" }}
                                                                >
                                                                    Documentos
                                                                </Link>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <TablePagination
                                        component="div"
                                        count={solicitudesPage.totalElements}
                                        page={solicitudesPage.number}
                                        onPageChange={(_e, newPage) =>
                                            cargarSolicitudes(newPage, solicitudesPage.size)
                                        }
                                        rowsPerPage={solicitudesPage.size}
                                        onRowsPerPageChange={(e) =>
                                            cargarSolicitudes(0, parseInt(e.target.value, 10))
                                        }
                                        rowsPerPageOptions={[5, 10, 20, 50, 100]}
                                        labelRowsPerPage="Filas por página"
                                        labelDisplayedRows={({ from, to, count }) =>
                                            `${Number.isNaN(from) ? 0 : from}–${Number.isNaN(to) ? 0 : to} de ${count}`
                                        }
                                    />
                                </>
                            )}
                        </>
                    )}
                </Box>
            </Box>

            {/* Modal ver descripción */}
            <Dialog
                open={modalAbierto}
                onClose={() => setModalAbierto(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>{subvencionSeleccionada?.nombre}</DialogTitle>
                <DialogContent dividers>
                    <div
                        className="tiptap-rendered"
                        dangerouslySetInnerHTML={{
                            __html: subvencionSeleccionada?.descripcion,
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalAbierto(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default SubvencionesAdmin;
